import { Collapse, Table, Tooltip } from "antd";
import { useCallback, useEffect, useState } from "react";
import {
  convertToLocalAndFormatDate,
  formatTime,
  getRiskLevel,
} from "../../utils/Utils";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  Application,
  ApplicationStatusId,
  ApplicationStatusText,
  ReduxApplication,
} from "../../types/Application";
import { LeaseEventId } from "../../types/Application";
import GenerateLeaseModal from "./GenerateLeaseModal";
import StatusTag from "../Shared/StatusTag";
import {
  generateLeaseDomainLease,
  getIsIssueResolutionEnabledForApplication,
  postLease,
  resendLeaseDomainLease,
  voidLease,
} from "../../services/AdminDashService";
import LeaseAddendumModal from "./LeaseAddendumModal";
import {
  fetchLeaseAddendumDetails,
  getApplicationById,
  saveAddendumAnswers,
} from "src/services/AdminDashService";
import type { LeaseAddendumDetails } from "src/types/LeaseDomainAddendum";
import { indexPendingAddendumsByApplicantId } from "src/utils/LeaseDomainAddendum";
import SubmitAddendumOnBehalfModal from "./SubmitAddendumOnBehalfModal";
import ConfirmCancelAddendumModal from "./ConfirmCancelAddendumModal";
import { isLeaseDomainApplicationId } from "src/utils/Utils";
import { setNotification } from "src/store/features/applications/applicationsSlice";
import { QuestionType } from "src/types/Addendum";
import { getAddendumAssociateAction } from "src/utils/Addendum";
import {
  hasUnresolvedHighRisk,
  getActualLeaseStatus,
} from "src/utils/Application";
import RentStabilizationModal from "./RentStabilizationModal";
import HouModal from "./HouModal";
import type { Configuration } from "src/types/Redux";
import { ApplicantType } from "src/types/Applicant";
import "./LeaseProcess.scss";

// Lease Domain has no lease.canGenerateLease equivalent - the real signal is the primary
// applicant's controls.canGenerateLease (already real for Lease Domain applications, see
// AP2-6392 second-tier fields). Guarantors always have this false per Lease Domain convention,
// so this specifically needs the Applicant-type record, matching getPrimaryApplicant()
// elsewhere (e.g. Screening.tsx).
const getPrimaryApplicantCanGenerateLease = (application: Application) =>
  application.applicants?.find(
    (a) => a.applicantTypeId === ApplicantType.Applicant && !a.isSoftDelete
  )?.controls?.canGenerateLease ?? false;

// Hoisted so LeaseProcess's eligibility check and LeaseButton's own render logic can't drift -
// legacy's lease.canGenerateLease OR'd with the Lease Domain controls fallback above.
const getCanGenerateLease = (application: Application) =>
  application.lease?.canGenerateLease ||
  getPrimaryApplicantCanGenerateLease(application);

// A locked application keeps the disabled Generate button where the associate would regenerate, not where Resend, Void or View apply.
const LOCKED_BUTTON_STATUSES = [
  ApplicationStatusId.Approved,
  ApplicationStatusId.LeaseDeclined,
  ApplicationStatusId.LeaseExpired,
  ApplicationStatusId.LeaseGenerationFailed,
];

function getGeneratedDate(leaseGeneratedDate) {
  return leaseGeneratedDate
    ? `${formatTime(leaseGeneratedDate)}, ${convertToLocalAndFormatDate(
        leaseGeneratedDate
      )}`
    : "";
}

interface LeaseProcessProps {
  application: ReduxApplication;
  isRentStabilizationCommunity: boolean;
  isChargesLoaded: boolean;
  configurationData?: Configuration;
}

interface ConfirmVoidLeaseModalProps {
  open: boolean;
  isVoiding: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ConfirmVoidLeaseModal = ({
  open,
  isVoiding,
  onClose,
  onConfirm,
}: ConfirmVoidLeaseModalProps): JSX.Element => (
  <Modal
    aria-labelledby="contained-modal-title-vcenter"
    centered
    show={open}
    onHide={isVoiding ? undefined : onClose}
    className="void-lease-modal"
  >
    <Modal.Header>
      <Modal.Title>
        <span className="cancel-icon">!</span>Void Lease?
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="details">
        <div className="row">
          <div className="col">
            Are you sure you want to void this lease&apos;s DocuSign envelope?
            This action cannot be undone.
          </div>
        </div>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="secondary"
        onClick={() => onClose()}
        disabled={isVoiding}
        data-testid="confirm-void-lease-modal-close-button"
      >
        Cancel
      </Button>
      <Button
        variant="dark"
        onClick={onConfirm}
        disabled={isVoiding}
        data-testid="confirm-void-lease-modal-confirm-button"
      >
        {isVoiding && (
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
          />
        )}
        {isVoiding ? "Voiding..." : "Void Lease"}
      </Button>
    </Modal.Footer>
  </Modal>
);

const LeaseProcess = ({
  application,
  isRentStabilizationCommunity,
  isChargesLoaded,
  configurationData,
}: LeaseProcessProps) => {
  const dispatch = useAppDispatch();
  const notification = useAppSelector(
    (state) => state.applications.notification
  );
  const { parking, addendums } = application ?? {};
  const [showGenerateLeaseModal, setShowGenerateLeaseModal] =
    useState<boolean>(false);
  const [showLeaseAddendumModal, setShowLeaseAddendumModal] =
    useState<boolean>(false);
  const [loadingAddendumSubmit, setLoadingAddendumSubmit] =
    useState<boolean>(false);
  const [showAddendumCancelModal, setShowAddendumCancelModal] =
    useState<boolean>(false);
  const [showRentStabilizationModal, setShowRentStabilizationModal] =
    useState<boolean>(false);
  const [previousLegalRent, setPreviousLegalRent] = useState<string>("");
  const [showHouModal, setShowHouModal] = useState<boolean>(false);
  const [monthlyChargesHou, setMonthlyChargesHou] = useState<boolean>(false);
  const addendumForms = addendums?.forms ?? [];
  const validLeaseStatus = [
    ApplicationStatusId.LeaseReady,
    ApplicationStatusId.LeasePending,
    ApplicationStatusId.LeaseSigned,
    ApplicationStatusId.LeaseExpired,
    ApplicationStatusId.LeaseDeclined,
  ];

  useEffect(() => {
    const { origin } = notification ?? {};
    if (origin === "postLease") {
      window.scrollTo(0, 0);
    }
    if (
      notification &&
      notification.message &&
      notification.type === "INFO" &&
      notification.message.endsWith(
        "Success! Lease addendum answers submitted"
      ) &&
      notification.origin === "saveAddendumAnswers"
    ) {
      setShowRentStabilizationModal(false);
      setShowLeaseAddendumModal(false);
      setLoadingAddendumSubmit(false);
    }
  }, [notification]);

  useEffect(() => {
    if (application?.monthlyCharges) {
      setMonthlyChargesHou(
        application?.monthlyCharges.some((charge) => charge.chgCode === "HOU")
      );
    }
  }, [application]);

  // AP2-6943: Lease Domain applications read their addendum answers from Lease Domain, not the legacy forms.
  const addendumStatus = application?.addendumStatus ?? null;
  const hasLeaseDomainAddendums =
    isLeaseDomainApplicationId(String(application?.applicationId)) &&
    (addendumStatus === "Pending" || addendumStatus === "Completed");
  const [leaseDomainAddendums, setLeaseDomainAddendums] =
    useState<LeaseAddendumDetails | null>(null);
  const [showOnBehalfModal, setShowOnBehalfModal] = useState<boolean>(false);

  useEffect(() => {
    const leaseId = application?.leaseId;
    if (!hasLeaseDomainAddendums || !leaseId) return;
    let isCurrent = true;
    dispatch(fetchLeaseAddendumDetails(leaseId)).then((details) => {
      if (isCurrent) setLeaseDomainAddendums(details);
    });
    return () => {
      isCurrent = false;
    };
  }, [application?.leaseId, hasLeaseDomainAddendums, addendumStatus]);

  // Hoisted above the early return below - all hooks must run on every render regardless of
  // isLeaseSectionEligible, or the hook count changes once configurationData/application load in.
  // Submit for the Rent Stabilization Rider / HOU dialogs. AP2-6893: a Lease Domain application must
  // generate through Lease Domain (same as GenerateLeaseModal) - legacy postLease 400s on its uuid id.
  // Routed by id format, not loaded config, same as resendLease below.
  const onSubmit = useCallback(
    (previousLegalRent?: string) => {
      const parsedPreviousLegalRent = previousLegalRent
        ? parseFloat(previousLegalRent)
        : undefined;

      if (isLeaseDomainApplicationId(String(application.applicationId))) {
        // A uuid id can only be generated by Lease Domain (legacy 400s on it), so a missing leaseId
        // is an error to show the associate, never a silent no-op or a fallback to legacy.
        if (!application.leaseId) {
          console.error(
            `LeaseDomain application ${application.applicationId} cannot generate: missing leaseId`
          );
          dispatch(
            setNotification({
              type: "ERROR",
              message: `Error generating lease, please try again`,
              origin: "postLease",
            })
          );
          return;
        }
        // The rider needs a previous legal rent above $0 (Lease Domain rejects anything else);
        // say so here instead of after a round trip.
        if (
          isRentStabilizationCommunity &&
          !(
            parsedPreviousLegalRent !== undefined &&
            Number.isFinite(parsedPreviousLegalRent) &&
            parsedPreviousLegalRent > 0
          )
        ) {
          dispatch(
            setNotification({
              type: "ERROR",
              message: `Enter a previous legal regulated rent greater than $0`,
              origin: "postLease",
            })
          );
          return;
        }
        dispatch(
          generateLeaseDomainLease(
            application.applicationId,
            application.leaseId,
            application.phaseId,
            configurationData,
            parsedPreviousLegalRent
          )
        );
        return;
      }

      dispatch(
        postLease(
          application.applicationId,
          LeaseEventId.LeaseGenerated,
          parsedPreviousLegalRent
        )
      );
    },
    [
      application?.applicationId,
      application?.leaseId,
      application?.phaseId,
      configurationData,
      isRentStabilizationCommunity,
    ]
  );

  // validLeaseStatus is legacy-only. For Lease Domain, mirror what LeaseButton itself would
  // render - any of these true means at least one action is available; none true (e.g.
  // cancelled) means an empty panel, so hide it same as legacy would. LeasePending (a lease out
  // for signature) stays visible regardless of canVoidLease/canResendLease so its lease status
  // is still shown, same as legacy's validLeaseStatus (AP2-6754).
  const isLeaseDomainApplication = isLeaseDomainApplicationId(
    String(application?.applicationId)
  );
  // A failed generation has no action to offer, but the associate still needs to see why.
  const isLeaseGenerationFailed =
    application?.applicationStatusId ===
    ApplicationStatusId.LeaseGenerationFailed;
  const isLeaseSectionEligible = isLeaseDomainApplication
    ? getCanGenerateLease(application) ||
      !!application?.lease?.canResendLease ||
      !!application?.lease?.canVoidLease ||
      application?.applicationStatusId === ApplicationStatusId.LeasePending ||
      application?.applicationStatusId === ApplicationStatusId.LeaseSigned ||
      application?.applicationStatusId === ApplicationStatusId.LeaseReady ||
      addendumStatus === "Pending" ||
      isLeaseGenerationFailed ||
      (!!application?.generationLockedReason &&
        LOCKED_BUTTON_STATUSES.includes(application?.applicationStatusId))
    : validLeaseStatus.includes(application?.applicationStatusId);

  if (!isLeaseSectionEligible) {
    return null;
  }
  const { Panel } = Collapse;

  const lastEvent = application?.lease?.leaseEvents?.[0] ?? null;
  const leaseGeneratedDate =
    application?.leaseGeneratedDate ||
    (application?.applicationStatusId === ApplicationStatusId.LeaseReady &&
      lastEvent?.leaseEventId === LeaseEventId.LeaseGenerated &&
      lastEvent?.createdDate) ||
    "";
  // AdminDashboard.API always emits leaseGeneratedDate as null for Lease Domain applications, so
  // their signal that a lease has been generated is the DocuSign package instead. Without a
  // generated lease, applicationStatusId is still an application-level (e.g. screening) status.
  const hasGeneratedLease =
    !!leaseGeneratedDate || !!application?.lease?.docusignPackageId;
  const showLeaseStatus =
    application?.applicationStatusId != undefined &&
    (!isLeaseDomainApplication || hasGeneratedLease || isLeaseGenerationFailed);

  const isAddendumsEnabled = addendumForms?.length > 0;
  const hasAddendumResponse = addendumForms.some(
    (addendumForm) => addendumForm.responses?.length
  );
  // The addendums endpoint only has a confirmed contract for legacy (numeric)
  // application ids; Lease Domain apps use a UUID id and have no supported
  // route here yet.
  const isNumericApplicationId = typeof application?.applicationId === "number";
  const hasUnresolvedScreeningIssues = (application: Application): boolean => {
    if (!application) return false;
    if (
      !isLeaseDomainApplicationId(String(application.applicationId)) &&
      (!configurationData?.features || !application.communityId)
    ) {
      return false;
    }

    const isFeatureEnabled = getIsIssueResolutionEnabledForApplication(
      configurationData?.features,
      application
    );

    if (!isFeatureEnabled) {
      return false;
    }

    return application?.applicants
      ?.filter((applicant) => !applicant.isSoftDelete)
      ?.some((applicant) =>
        applicant?.applicantScreening?.some((screening) =>
          screening?.issues?.some((issue) => !issue.resolved)
        )
      );
  };

  // for each addendum form:
  //   if no responses, render "None"
  //   for each response:
  //     if single-line or multi-line, render each response as a sub-row
  //     if multi-select or single-select, render each answerText
  const addendumTableRows = !hasAddendumResponse
    ? []
    : addendumForms.map((form) => ({
        addendum: form.formName,
        ["resident-response"]: !form?.responses?.length
          ? "None"
          : form.responses.map((response) => {
              const question = form?.questions?.find((question) =>
                question.answers?.map((a) => a.id).includes(response.answerId)
              );
              if (!question) return null;
              const answer = question.answers?.find(
                (answer) => answer.id === response.answerId
              );
              if (!answer) return null;
              switch (question.questionType) {
                case QuestionType.SingleLine:
                case QuestionType.MultiLine:
                  return (
                    <div key={`table-answer-${answer.id}`}>
                      {response.answerValue}
                    </div>
                  );
                case QuestionType.SingleSelect:
                  return (
                    <div key={`table-answer-${answer.id}`}>
                      {answer.answerText}
                    </div>
                  );
                case QuestionType.MultiSelect:
                  return (
                    <div key={`table-answer-${answer.id}`}>
                      {answer.answerText}
                    </div>
                  );
                default:
                  return null;
              }
            }),
        ["associate-action"]: !form?.responses?.length
          ? "N/A"
          : getAddendumAssociateAction(form.responses),
      }));

  const renderStatusTag = (): JSX.Element => {
    const nameMapper: { [appStatusId: number]: string } = {
      [ApplicationStatusId.InviteSent]: ApplicationStatusText.INVITE_SENT,
      [ApplicationStatusId.InProgress]: ApplicationStatusText.IN_PROGRESS,
      [ApplicationStatusId.Rejected]: ApplicationStatusText.REJECTED,
      [ApplicationStatusId.Completed]: ApplicationStatusText.COMPLETED,
      [ApplicationStatusId.Removed]: ApplicationStatusText.REMOVED,
      [ApplicationStatusId.CreditAndIDReturned]:
        ApplicationStatusText.CREDIT_AND_ID_RETURNED,
      [ApplicationStatusId.LeasePending]: ApplicationStatusText.LEASE_PENDING,
      [ApplicationStatusId.LeaseSigned]: ApplicationStatusText.LEASE_SIGNED,
      [ApplicationStatusId.CreditAndIDPending]:
        ApplicationStatusText.CREDIT_AND_ID_PENDING,
      [ApplicationStatusId.Cancelled]: ApplicationStatusText.CANCELLED,
      [ApplicationStatusId.Approved]: ApplicationStatusText.APPROVED,
      [ApplicationStatusId.Closed]: ApplicationStatusText.CLOSED,
      [ApplicationStatusId.ActionRequired]:
        ApplicationStatusText.ACTION_REQUIRED,
      [ApplicationStatusId.ApplicationComplete]:
        ApplicationStatusText.APPLICATION_COMPLETE,
      [ApplicationStatusId.LeaseReady]: ApplicationStatusText.LEASE_READY,
      [ApplicationStatusId.LeaseExpired]: ApplicationStatusText.LEASE_EXPIRED,
      [ApplicationStatusId.LeaseDeclined]: ApplicationStatusText.LEASE_DECLINED,
      [ApplicationStatusId.LeaseGenerationFailed]:
        ApplicationStatusText.LEASE_GENERATION_FAILED,
    };
    let name: string =
      nameMapper[application?.applicationStatusId] ??
      ApplicationStatusText.IN_PROGRESS;
    // if in lease-ready state and addendums is enabled, render Addendum status
    if (name === ApplicationStatusText.LEASE_READY && isAddendumsEnabled) {
      // if parking has been selected, render Pending Addendum Selection
      if (
        parking != null &&
        !parking.optInApplicantId &&
        !parking.optOutApplicantId
      ) {
        name = "Pending Addendums";
      } else if (!hasAddendumResponse) {
        name = "Pending Addendum Response";
      }
    }
    // No envelope exists after a failed generation, so the voided/expired lease override cannot apply.
    if (isLeaseGenerationFailed) {
      return (
        <Tooltip
          title={application?.leaseGenerationFailureDescription}
          placement="top"
        >
          <div
            style={{ display: "inline-block" }}
            tabIndex={0}
            aria-label={
              application?.leaseGenerationFailureDescription
                ? `${name}: ${application.leaseGenerationFailureDescription}`
                : name
            }
          >
            <StatusTag name={name} />
          </div>
        </Tooltip>
      );
    }
    // Check if we should show "Lease Voided" based on esignStatus
    name = getActualLeaseStatus(name, application);
    return <StatusTag name={name} />;
  };

  // One addendum entry exists per applicant; the form and its answers are application-wide.
  const leaseDomainForms = Array.from(
    new Map(
      (leaseDomainAddendums?.addendums ?? []).map((addendum) => [
        addendum.addendumId,
        addendum,
      ])
    ).values()
  );
  const leaseDomainAddendumRows = leaseDomainForms.map((form) => {
    // Keyed by questionId: question text is display content and need not be unique.
    const answersByQuestion = new Map<string, { values: string[] }>();
    for (const answer of leaseDomainAddendums?.submission?.answers ?? []) {
      if (answer.addendumId !== form.addendumId) continue;
      const entry = answersByQuestion.get(answer.questionId) ?? {
        values: [],
      };
      entry.values.push(answer.answerValue);
      answersByQuestion.set(answer.questionId, entry);
    }
    return {
      addendum: form.formTitle ?? form.addendumId,
      ["resident-response"]: answersByQuestion.size
        ? Array.from(answersByQuestion, ([questionId, { values }]) => (
            <div key={`${form.addendumId}-${questionId}`}>
              {values.join(", ")}
            </div>
          ))
        : addendumStatus === "Completed"
        ? "None"
        : "Pending",
      ["associate-action"]: "N/A",
    };
  });
  const pendingAddendumsByApplicantId = indexPendingAddendumsByApplicantId(
    leaseDomainAddendums?.addendums ?? []
  );
  const onBehalfApplicantId = Object.keys(pendingAddendumsByApplicantId)[0];
  const onBehalfApplicant = application?.applicants?.find(
    (applicant) => String(applicant.id) === onBehalfApplicantId
  );

  return (
    <div id="avb-lease-process">
      <Collapse defaultActiveKey={"2"} bordered={false}>
        <Panel header={`LEASE GENERATION PROCESS`} key="2">
          <div className="row">
            <div className="blue-card col-sm-12 col-md-4">
              <h3>Lease Generation Process</h3>
              <p>LEASE STATUS</p>
              {showLeaseStatus && (
                <div className="tagPlaceHolder mb-1">{renderStatusTag()}</div>
              )}
              {application?.addOnsUnavailableReason && (
                <p
                  className="text-muted"
                  data-testid="add-ons-unavailable-reason"
                >
                  {application.addOnsUnavailableReason}
                </p>
              )}
              {leaseGeneratedDate && (
                <p>
                  LEASE GENERATED TIME
                  <span>{getGeneratedDate(leaseGeneratedDate)}</span>
                </p>
              )}
            </div>
            {hasLeaseDomainAddendums && (
              <div
                className="addendum-table_wrapper d-flex flex-column col-sm-12 justify-content-center col-md-8"
                data-testid="lease-domain-addendums"
              >
                <Table
                  id="addendum-information"
                  dataSource={leaseDomainAddendumRows}
                  columns={[
                    {
                      title: "Addendum",
                      dataIndex: "addendum",
                      key: "addendum",
                    },
                    {
                      title: "Resident response",
                      dataIndex: "resident-response",
                      key: "resident-response",
                    },
                    {
                      title: "Associate action",
                      dataIndex: "associate-action",
                      key: "associate-action",
                    },
                  ]}
                  pagination={false}
                  rowKey="addendum"
                />
                {addendumStatus === "Pending" && (
                  <div className="d-flex justify-content-center mt-4">
                    <Button
                      data-testid="review-addendum-button"
                      variant="dark"
                      onClick={() => setShowOnBehalfModal(true)}
                      disabled={!onBehalfApplicantId}
                    >
                      Review Lease Addendums
                    </Button>
                  </div>
                )}
              </div>
            )}
            {isAddendumsEnabled && (
              <div className="addendum-table_wrapper d-flex flex-column col-sm-12 justify-content-center col-md-8">
                <Table
                  id="addendum-information"
                  dataSource={addendumTableRows}
                  columns={[
                    {
                      title: "Addendum",
                      dataIndex: "addendum",
                      key: "addendum",
                    },
                    {
                      title: "Resident response",
                      dataIndex: "resident-response",
                      key: "resident-response",
                    },
                    {
                      title: "Associate action",
                      dataIndex: "associate-action",
                      key: "associate-action",
                    },
                  ]}
                  pagination={false}
                  rowKey="addendum"
                />
                {!hasAddendumResponse && (
                  <div className="d-flex justify-content-center mt-4">
                    <Button
                      data-testid="review-addendum-button"
                      variant="dark"
                      onClick={() => setShowLeaseAddendumModal(true)}
                      // Gate the entry point for Lease Domain (UUID) apps here instead of
                      // letting the associate fill out the form and find out only after
                      // clicking Submit.
                      disabled={
                        !addendumForms?.length || !isNumericApplicationId
                      }
                      title={
                        !isNumericApplicationId
                          ? "Addendum submission is not yet supported for this application"
                          : undefined
                      }
                    >
                      Review Lease Addendums
                    </Button>
                  </div>
                )}
              </div>
            )}
            {!!application?.lease && (
              <div className="lease-button_container">
                <LeaseButton
                  application={application}
                  onClick={() =>
                    isRentStabilizationCommunity
                      ? setShowRentStabilizationModal(true)
                      : monthlyChargesHou
                      ? setShowHouModal(true)
                      : setShowGenerateLeaseModal(true)
                  }
                  isChargesLoaded={isChargesLoaded}
                  hasUnresolvedIssues={hasUnresolvedScreeningIssues(
                    application
                  )}
                  hasUnresolvedHighRisk={hasUnresolvedHighRisk(
                    application,
                    configurationData
                  )}
                  configuration={configurationData}
                />
              </div>
            )}
          </div>
        </Panel>
      </Collapse>
      <GenerateLeaseModal
        selectedApplication={application}
        configuration={configurationData}
        onClose={() => setShowGenerateLeaseModal(false)}
        show={showGenerateLeaseModal}
      />
      <RentStabilizationModal
        open={showRentStabilizationModal}
        onClose={() => setShowRentStabilizationModal(false)}
        selectedApplication={application}
        onSubmit={(previousLegalRent) => {
          if (monthlyChargesHou) {
            setPreviousLegalRent(previousLegalRent);
            setShowHouModal(true);
          } else {
            onSubmit(previousLegalRent);
          }
        }}
        setPreviousLegalRent={setPreviousLegalRent}
        previousLegalRent={previousLegalRent}
      />
      <HouModal
        open={showHouModal}
        onClose={() => {
          setPreviousLegalRent("");
          setShowHouModal(false);
        }}
        onConfirm={
          isRentStabilizationCommunity
            ? () => {
                setShowHouModal(false);
                onSubmit(previousLegalRent);
              }
            : () => {
                setShowHouModal(false);
                onSubmit();
              }
        }
      />

      {hasLeaseDomainAddendums && (
        <SubmitAddendumOnBehalfModal
          show={showOnBehalfModal}
          onHide={() => setShowOnBehalfModal(false)}
          leaseId={application?.leaseId ?? ""}
          applicantId={onBehalfApplicantId ?? ""}
          applicantName={`${onBehalfApplicant?.firstName ?? ""} ${
            onBehalfApplicant?.lastName ?? ""
          }`.trim()}
          addendums={pendingAddendumsByApplicantId[onBehalfApplicantId] ?? []}
          onSubmitted={() => {
            if (application?.leaseId) {
              dispatch(fetchLeaseAddendumDetails(application.leaseId)).then(
                setLeaseDomainAddendums
              );
            }
            if (application?.applicationId) {
              dispatch(getApplicationById(application.applicationId));
            }
          }}
        />
      )}

      {isAddendumsEnabled && (
        <>
          <LeaseAddendumModal
            open={showLeaseAddendumModal}
            addendums={addendumForms}
            onSubmit={(isValidForm, formValues) => {
              // isNumericApplicationId is redundant here in practice - the Review Lease
              // Addendums button above is disabled for non-numeric ids, so this modal can't
              // open otherwise - but it's kept so saveAddendumAnswers (applicationId: number)
              // still type-checks, and as a defensive backstop.
              if (isValidForm && formValues && isNumericApplicationId) {
                dispatch(
                  saveAddendumAnswers(application.applicationId, formValues)
                );
                setLoadingAddendumSubmit(true);
              } else {
                dispatch(
                  setNotification({
                    type: "ERROR",
                    message: !isNumericApplicationId
                      ? "Addendum submission is not yet supported for this application"
                      : "Error submitting answers, please try again later",
                  })
                );
              }
            }}
            onClose={() => setShowAddendumCancelModal(true)}
            loading={loadingAddendumSubmit}
          />
          <ConfirmCancelAddendumModal
            open={showAddendumCancelModal}
            onClose={() => setShowAddendumCancelModal(false)}
            onCancel={() => setShowLeaseAddendumModal(false)}
          />
        </>
      )}
    </div>
  );
};

interface LeaseButtonProps {
  application: Application;
  onClick: () => void;
  isChargesLoaded: boolean;
  hasUnresolvedIssues?: boolean;
  hasUnresolvedHighRisk?: boolean;
  configuration?: Configuration;
}
const LeaseButton = ({
  application,
  onClick,
  isChargesLoaded,
  hasUnresolvedIssues = false,
  hasUnresolvedHighRisk = false,
  configuration,
}: LeaseButtonProps) => {
  const { applicationId, applicationStatusId, lease } = application;
  if (applicationStatusId == null || applicationId == null) {
    return null;
  }

  const dispatch = useAppDispatch();
  const [isVoiding, setIsVoiding] = useState(false);
  const [showVoidConfirmModal, setShowVoidConfirmModal] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const resendLease = () => {
    // Route by id format, not loaded config: an empty config sent a Lease Domain app to legacy (400).
    if (isLeaseDomainApplicationId(String(applicationId))) {
      if (!application.leaseId || !lease?.docusignPackageId) {
        console.error(
          `LeaseDomain application ${applicationId} is missing leaseId or docusignPackageId; cannot resend`
        );
        dispatch(
          setNotification({
            type: "ERROR",
            message: `Error sending out lease, please try again`,
            origin: "postLease",
          })
        );
        return;
      }
      setIsResending(true);
      dispatch(
        resendLeaseDomainLease(
          application.leaseId,
          lease.docusignPackageId,
          applicationId
        )
      ).finally(() => setIsResending(false));
      return;
    }

    dispatch(postLease(applicationId, LeaseEventId.LeaseResent));
  };

  const handleVoidLease = async () => {
    setIsVoiding(true);
    // The modal closes once Lease Domain answers; the button stays on "Void in Progress" until the status really changes.
    await dispatch(
      voidLease(applicationId, application.communityId, () =>
        setShowVoidConfirmModal(false)
      )
    );
    setIsVoiding(false);
    setShowVoidConfirmModal(false);
  };

  // AP2-6912: Lease Domain leases carry the signed PDF URL, legacy ones a lease event's dsDocPath
  const signedLeaseUrl = isLeaseDomainApplicationId(String(applicationId))
    ? lease?.signedDocumentUrl
    : lease?.leaseEvents?.[0]?.dsDocPath;

  const viewLease = () => {
    signedLeaseUrl && window.open(signedLeaseUrl, "_blank");
  };

  const areConcessionsNotInMri =
    application.areAppliedConcessionsInMri === false &&
    application?.appliedConcessions?.length > 0; // only disable if there are concessions and they are not in MRI. If there are no concessions, we should not disable lease generation  .

  const canGenerateLease = getCanGenerateLease(application);
  // AP2-6943: Lease Domain withholds Generate until the addendums are answered.
  const isAddendumsPending =
    isLeaseDomainApplicationId(String(applicationId)) &&
    application.addendumStatus === "Pending";

  const generationLockedReason = application.generationLockedReason ?? "";

  const isGenerateLeaseDisabled =
    !!generationLockedReason ||
    isAddendumsPending ||
    !isChargesLoaded ||
    !canGenerateLease ||
    hasUnresolvedIssues ||
    hasUnresolvedHighRisk ||
    areConcessionsNotInMri;

  let disabledReason = "";
  if (generationLockedReason) {
    disabledReason = generationLockedReason;
  } else if (hasUnresolvedHighRisk) {
    disabledReason =
      "Cannot generate lease: Applicant(s) have unresolved high-risk scores that require further review clearance.";
  } else if (hasUnresolvedIssues) {
    disabledReason =
      "Cannot generate lease: Please resolve all screening issues first.";
  } else if (areConcessionsNotInMri) {
    disabledReason =
      "Cannot generate lease: Specials have not been entered into MRI yet.";
  } else if (!isChargesLoaded) {
    disabledReason = "Loading charges...";
  } else if (isAddendumsPending) {
    disabledReason =
      "Cannot generate lease: the lease addendums must be answered first.";
  } else if (!canGenerateLease) {
    disabledReason = "Lease cannot be generated at this time.";
  }

  // canVoidLease is sourced from Lease Domain's own lease + DocuSign package status (leaseStatus
  // is "Scheduled" and the envelope hasn't completed yet), so it already reflects "not voidable"
  // once a lease is voided, activated, vacated, or fully signed - no need to separately check
  // getActualLeaseStatus/leaseEvents here, which don't exist on a Lease Domain application's
  // lease object anyway.
  const voidButton = lease?.canVoidLease ? (
    <>
      <Button
        data-testid="void-lease-button"
        variant="outline-danger"
        onClick={() => setShowVoidConfirmModal(true)}
        disabled={isVoiding}
        style={{ marginLeft: "15px" }}
      >
        {isVoiding ? "Void in Progress" : "Void"}
      </Button>
      <ConfirmVoidLeaseModal
        open={showVoidConfirmModal}
        isVoiding={isVoiding}
        onClose={() => setShowVoidConfirmModal(false)}
        onConfirm={handleVoidLease}
      />
    </>
  ) : null;

  if (
    canGenerateLease ||
    (generationLockedReason &&
      LOCKED_BUTTON_STATUSES.includes(applicationStatusId) &&
      !lease?.canResendLease) ||
    isAddendumsPending ||
    applicationStatusId === ApplicationStatusId.LeaseReady
  ) {
    const button = (
      <Button
        data-testid="generate-lease-button"
        variant="dark"
        onClick={onClick}
        disabled={isGenerateLeaseDisabled}
      >
        Generate Lease in DocuSign
      </Button>
    );

    return (
      <>
        {disabledReason ? (
          <Tooltip title={disabledReason} placement="top">
            <div style={{ display: "inline-block" }}>{button}</div>
          </Tooltip>
        ) : (
          button
        )}
        {voidButton}
      </>
    );
  } else if (lease?.canResendLease) {
    return (
      <>
        <Button
          data-testid="resend-lease-button"
          variant="outline-dark"
          onClick={resendLease}
          disabled={isResending || isVoiding}
          style={{
            marginRight: "15px",
          }}
        >
          {isResending ? "Sending..." : "Resend Email"}
        </Button>
        {voidButton}
      </>
    );
  }

  if (applicationStatusId === ApplicationStatusId.LeaseSigned) {
    const viewLeaseButton = (
      <Button
        variant="dark"
        onClick={viewLease}
        disabled={!signedLeaseUrl}
        data-testid="view-lease-button"
      >
        View Lease
      </Button>
    );

    return (
      <>
        {signedLeaseUrl ? (
          viewLeaseButton
        ) : (
          <Tooltip
            title="The signed lease document is not available."
            placement="top"
          >
            <div
              style={{ display: "inline-block" }}
              tabIndex={0}
              aria-label="View Lease unavailable: the signed lease document is not available."
            >
              {viewLeaseButton}
            </div>
          </Tooltip>
        )}
        {voidButton}
      </>
    );
  }

  return voidButton;
};

export default LeaseProcess;
